import Navbar from "@/components/Navbar";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { UserPlus, Globe, Zap, Plane, Truck, Sprout, Lightbulb } from "lucide-react";
import DomainElements from "@/components/gsa/DomainElements";
import { Domain } from "@/types/domain";
import KnowledgeNetworkStats from "@/components/gsa/KnowledgeNetworkStats";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "@/hooks/useTranslation";
import { useLanguage } from "@/contexts/LanguageContext";

// Quebec economic diplomacy domains focused on Southern U.S. markets
const domains: Domain[] = [{
  symbol: "Clean Energy & Grid Infrastructure",
  name: "Clean Energy & Grid Infrastructure",
  categories: "Québec Hydroelectric Transmission, Champlain Hudson Power Express, Boralex and Innergex U.S. Operations, Battery Storage Partnerships, Cap-and-Trade with California"
}, {
  symbol: "Electric & Sustainable Transportation",
  name: "Electric & Sustainable Transportation",
  categories: "Lion Electric Manufacturing, Quebec Battery Materials to Tesla, Cross-border EV Charging, Lithium Mining Nord-du-Québec, Montreal Metro Electrification"
}, {
  symbol: "Aerospace & Advanced Aviation",
  name: "Aerospace & Advanced Aviation",
  categories: "Bombardier-Airbus Mobile Alabama, Pratt & Whitney Canada Engines, Bell Helicopter Quebec, CAE Flight Simulation U.S., Quebec Aerospace Cluster"
}, {
  symbol: "Critical & Strategic Minerals",
  name: "Critical & Strategic Minerals",
  categories: "Rare Earth Elements Mining, Graphite Battery Supply Chains, Mining Equipment Export, Critical Minerals Processing, Quebec-U.S. Strategic Partnership"
}, {
  symbol: "AI, ICT, and Smart Industry",
  name: "AI, ICT, and Smart Industry",
  categories: "Mila AI Institute Collaborations, Quebec Fintech U.S. Markets, Ubisoft Montreal Global, Quantum Computing Sherbrooke, 5G Infrastructure Development"
}, {
  symbol: "Pulp, Paper & Forestry Innovation",
  name: "Pulp, Paper & Forestry Innovation",
  categories: "Sustainable Forestry Certification, Biofuel from Forest Residues, High-Tech Wood Products, Carbon Capture Forest Operations, Clean Technology Adoption"
}, {
  symbol: "Aluminum & Industrial Manufacturing",
  name: "Aluminum & Industrial Manufacturing",
  categories: "Rio Tinto Saguenay Smelting, Aluminum Recycling Technology, Light-weight Automotive Components, Green Aluminum Production, Aerospace-grade Alloys"
}, {
  symbol: "Life Sciences & Biomedical Supply Chain",
  name: "Life Sciences & Biomedical Supply Chain",
  categories: "GSK Novartis Quebec Vaccines, Biosimilar Drug Development, Medical Device Manufacturing, Clinical Trial Partnerships, Biotech Incubator Programs"
}, {
  symbol: "Agri-Food & Bioeconomy",
  name: "Agri-Food & Bioeconomy",
  categories: "Maple Syrup Global Export, Plant-based Protein Processing, Sustainable Agriculture Technology, Food Safety Traceability, Precision Agriculture IoT"
}, {
  symbol: "Green Economy & Climate Diplomacy",
  name: "Green Economy & Climate Diplomacy",
  categories: "International Climate Leadership, Carbon Credit Trading, Green Finance Investment, Climate Adaptation Technology, Environmental Cooperation Agreements"
}, {
  symbol: "Logistics, Ports, and Maritime Trade",
  name: "Logistics, Ports, and Maritime Trade",
  categories: "Port of Montreal Expansion, St. Lawrence Seaway Optimization, Cold Chain Food Export, Multi-modal Transportation Hubs, Arctic Shipping Routes"
}, {
  symbol: "Public Transit & Urban Mobility",
  name: "Public Transit & Urban Mobility",
  categories: "Montreal REM Light Rail Export, Smart Traffic Management, Electric Bus Fleet Deployment, Bike-sharing Micro-mobility, Integrated Urban Planning"
}, {
  symbol: "Academic & Research Collaboration",
  name: "Academic & Research Collaboration",
  categories: "McGill-Harvard Medical Research, Université de Montréal AI Exchange, HEC Montréal Business Programs, École Polytechnique Innovation, Cross-border Student Exchange"
}, {
  symbol: "Tourism, Culture & Francophonie",
  name: "Tourism, Culture & Francophonie",
  categories: "Quebec Winter Carnival Tourism, Cirque du Soleil Global Entertainment, French Language Cultural Exchange, Cultural Heritage Technology, Indigenous Tourism Development"
}, {
  symbol: "Public Procurement & Economic Diplomacy",
  name: "Public Procurement & Economic Diplomacy",
  categories: "Quebec-U.S. Government Procurement, Buy Quebec/Buy America Alignment, Trade Mission Business Development, Export Credit Financing, Economic Partnership Investment"
}];
const networkStats = {
  totalParticipants: 1247,
  activeContributors: 568,
  knowledgeBases: 892,
  totalSolutions: 324,
  departments: [{
    name: "Investissement Québec",
    participants: 298
  }, {
    name: "Ministère de l'Économie et de l'Innovation",
    participants: 245
  }, {
    name: "Québec Government Office - Houston",
    participants: 187
  }, {
    name: "Export Québec",
    participants: 156
  }, {
    name: "Other Quebec Trade Offices",
    participants: 361
  }]
};
const getTeamMembers = (t: (key: string) => string) => [{
  name: "Eglantine Pauvarel Moss",
  role: t('transportationAttache'),
  avatar: "/lovable-uploads/c7a8d7e2-ac1a-4e7a-b438-93cc1702bb99.png"
}, {
  name: "Karen Watson",
  role: t('coFounder'),
  avatar: "/lovable-uploads/aa92db57-7190-48b6-b5fc-3715e0595b53.png"
}, {
  name: "Pete Dumont",
  role: t('advisor'),
  avatar: "/lovable-uploads/5f30f2e5-7785-473b-a3aa-2567bb7210db.png"
}, {
  name: "Trey Fleming",
  role: t('founderCeo'),
  avatar: "/lovable-uploads/2fa48558-0c06-47c5-8c0c-39032005d8c7.png"
}, {
  name: "Ozlem Tuncel Toplu",
  role: t('directorEconomic'),
  avatar: "/lovable-uploads/a044a687-d14a-497e-9bb5-eec5d53ab9aa.png"
}];
const QuebecChannel = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const teamMembers = getTeamMembers(t);

  // Call for Solutions data for demo
  const cfsDemoData = {
    "type": "call_for_solutions",
    "title": "Decarbonizing Port Infrastructure in the Gulf",
    "description": "The Quebec Government Office in Houston is seeking proposals from Quebec companies addressing port decarbonization. Priority areas: smart maritime logistics, electrified ground equipment, clean battery systems.",
    "deadline": "2025-10-15",
    "submission_format": "interactive pitch deck + 500-word impact summary",
    "eligibility": ["Quebec-based SMEs", "Academic-Industry Consortia", "Crown Corp Ventures"],
    "sectors": ["transportation", "maritime", "clean energy", "ICT"],
    "public_visibility": "semi-private",
    "review_team": ["Quebec Delegation – Houston", "Port of Houston", "Texas GLO Innovation"]
  };
  const handleCfsClick = () => {
    // Store the demo data in sessionStorage for the demo page to use
    sessionStorage.setItem('quebec-cfs-demo-data', JSON.stringify(cfsDemoData));
    navigate('/demo-call-for-solutions-quebec');
  };
  return <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-6">
              <Link to="/quebec-channel">
                <img src="/lovable-uploads/68dead0e-7dd0-4056-8548-bf8f071aa08f.png" alt="Quebec Economic Development Giblet" className="w-28 h-28 object-contain cursor-pointer hover:opacity-80 transition-opacity" />
              </Link>
              <div className="flex-grow">
                <CardTitle className="text-2xl font-bold mb-2">{t('quebec.title')}</CardTitle>
                <CardDescription className="text-lg">
                  {t('quebec.description')}
                </CardDescription>
                <div className="flex items-center gap-2 mt-3">
                  <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                    <Globe className="w-3 h-3 mr-1" />
                    {t('economicDiplomacy')}
                  </Badge>
                  <Badge variant="secondary" className="bg-green-100 text-green-800">
                    <Zap className="w-3 h-3 mr-1" />
                    {t('crossBorderInnovation')}
                  </Badge>
                  <Badge variant="secondary" className="bg-purple-100 text-purple-800">
                    <Globe className="w-3 h-3 mr-1" />
                    {t('frenchEnglish')}
                  </Badge>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">{t('quebec.dashboard')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="col-span-2">
                  <KnowledgeNetworkStats stats={networkStats} />
                </div>
                <div className="flex flex-col gap-4">
                  <Card className="p-3 bg-blue-50 border-l-4 border-l-blue-500">
                    <div className="flex items-center space-x-3 mb-2">
                      <Avatar className="h-12 w-12 border-2 border-blue-500">
                        <AvatarImage src="/lovable-uploads/4f7af710-d2e5-443b-bfab-9b66d2464b8d.png" alt="Steven Jast" className="object-cover" />
                        <AvatarFallback className="bg-blue-100 text-blue-800 text-xl">SL</AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="text-lg font-semibold">{t('quebec.ambassador')}</h3>
                        <Link to="/steven-profile" className="text-primary hover:text-primary/80 font-medium">Steven Jast</Link>
                        <p className="text-sm text-gray-600">{t('quebec.director')}</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 mb-3">
                      {t('quebec.leading')}
                    </p>
                    <Button variant="outline" size="sm" className="w-full mb-3">
                      <UserPlus className="mr-2 h-4 w-4" />
                      {t('quebec.join')}
                    </Button>
                    
                    <div className="mt-4 pt-3 border-t border-gray-200">
                      <h4 className="text-sm font-medium mb-2">{t('quebec.knowledge')}</h4>
                      <div className="space-y-2">
                        {teamMembers.map((member, index) => <div key={index} className="flex items-center space-x-2">
                            <Avatar className="h-6 w-6">
                              <AvatarImage src={index === 0 ? "/lovable-uploads/3f677550-3b0f-427b-8766-02b635745dff.png" : index === 1 ? "/lovable-uploads/0f683b2b-caaf-4bcc-b3c3-4952ef16e3b8.png" : index === 2 ? "/lovable-uploads/129d20f3-6917-4d4e-accd-bf59fa3feaa7.png" : index === 3 ? "/lovable-uploads/7bc103ea-7508-4c35-90f7-b24ef821a676.png" : "/lovable-uploads/a044a687-d14a-497e-9bb5-eec5d53ab9aa.png"} alt={member.name} />
                              <AvatarFallback className="text-xs">{member.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <Link to={index === 0 ? "/eglantine-profile" : index === 1 ? "/karen-profile" : index === 2 ? "/pete-profile" : index === 3 ? "/trey-profile" : "/ozlem-profile"} className="text-xs font-medium hover:text-primary truncate">{member.name}</Link>
                              <p className="text-xs text-gray-500 truncate">{member.role}</p>
                            </div>
                          </div>)}
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </div>

            {/* Demo Use Cases */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">{t('quebec.demo')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-4 border-l-4 border-l-green-500">
                  <div className="flex items-center mb-3">
                    <Lightbulb className="h-5 w-5 text-green-600 mr-2" />
                    <h3 className="font-semibold text-sm">{t('quebec.callForKnowledge')}</h3>
                  </div>
                  <h4 className="font-medium mb-2">"{t('quebec.cleanEnergyExportStrategy')}"</h4>
                  <p className="text-xs text-gray-600 mb-3">
                    {t('quebec.embeddedInfoVerified')}
                  </p>
                  <div className="flex gap-1 flex-wrap">
                    <Badge variant="outline" className="text-xs">Hydro-Québec</Badge>
                    <Badge variant="outline" className="text-xs">Boralex</Badge>
                  </div>
                </Card>

                <Card className="p-4 border-l-4 border-l-orange-500 cursor-pointer hover:shadow-md transition-shadow" onClick={handleCfsClick}>
                  <div className="flex items-center mb-3">
                    <Globe className="h-5 w-5 text-orange-600 mr-2" />
                    <h3 className="font-semibold text-sm">{t('quebec.callForSolutions')}</h3>
                  </div>
                  <h4 className="font-medium mb-2">
                    <button onClick={handleCfsClick} className="text-blue-600 hover:text-blue-800 underline cursor-pointer">
                      "{t('quebec.decarbonizingPortInfrastructure')}"
                    </button>
                  </h4>
                  <p className="text-xs text-gray-600 mb-3">
                    {t('quebec.invitingQuebecCompanies')}
                  </p>
                  <div className="flex gap-1 flex-wrap">
                    <Badge variant="outline" className="text-xs">{t('quebec.maritimeTech')}</Badge>
                    <Badge variant="outline" className="text-xs">{t('quebec.knowledgeNetwork')}</Badge>
                  </div>
                </Card>

                <Card className="p-4 border-l-4 border-l-blue-500">
                  <div className="flex items-center mb-3">
                    <UserPlus className="h-5 w-5 text-blue-600 mr-2" />
                    <h3 className="font-semibold text-sm">{t('quebec.privateChannel')}</h3>
                  </div>
                  <h4 className="font-medium mb-2">"{t('quebec.houstonEconomicDiplomacy')}"</h4>
                  <p className="text-xs text-gray-600 mb-3">
                    {t('quebec.bilingualBriefingMaterials')}
                  </p>
                  <div className="flex gap-1 flex-wrap">
                    <Badge variant="outline" className="text-xs">FR/EN</Badge>
                    <Badge variant="outline" className="text-xs">{t('quebec.secure')}</Badge>
                  </div>
                </Card>
              </div>
            </div>

            {/* Key Market Stats */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">{t('quebec.tradeHighlights')}</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">$8.2B</div>
                  <div className="text-sm text-gray-600">{t('quebec.texasTradeVolume')}</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">450+</div>
                  <div className="text-sm text-gray-600">{t('quebec.quebecCompanies')}</div>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">65%</div>
                  <div className="text-sm text-gray-600">{t('quebec.cleanEnergyExports')}</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">28</div>
                  <div className="text-sm text-gray-600">{t('quebec.tradeMissions')}</div>
                </div>
              </div>
            </div>
            
            <div className="mt-8">
              <h2 className="text-xl font-semibold mb-4">{t('quebec.sectorExcellence')}</h2>
              <DomainElements domains={domains} />
            </div>

            {/* Call to Action */}
            <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border">
              <h3 className="text-lg font-semibold mb-3">{t('quebec.transform')}</h3>
              <p className="text-gray-700 mb-4">
                {t('quebec.discover')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button className="flex-1">
                  {t('quebec.startPilot')}
                </Button>
                <Button variant="outline" className="flex-1">
                  {t('quebec.scheduleSession')}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {t('quebec.pilotInvestment')}
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>;
};
export default QuebecChannel;
